import React, { useEffect, useState } from "react";
import {
    Avatar,
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Autocomplete,
    InputAdornment,
    MenuItem,
    TextField,
    Typography,
} from "@mui/material";
import { Pets as PetsIcon } from "@mui/icons-material";
import { Pet, PetPayload } from "../../types";
import { petService } from "../../services/petService";
import { ImageUploadZone } from "../product/ImageUploadZone";

const SPECIES_SUGGESTIONS = ["Dog", "Cat", "Bird", "Rabbit", "Fish", "Reptile", "Other"];

interface PetProfileEditorProps {
    open: boolean;
    onClose: () => void;
    customerId: string;
    pet: Pet | null; // null = create mode
    onSaved: (pet: Pet) => void;
}

export const PetProfileEditor: React.FC<PetProfileEditorProps> = ({
    open,
    onClose,
    customerId,
    pet,
    onSaved,
}) => {
    const [name, setName] = useState("");
    const [species, setSpecies] = useState("");
    const [breed, setBreed] = useState("");
    const [birthday, setBirthday] = useState("");
    const [gender, setGender] = useState<"" | "male" | "female">("");
    const [weight, setWeight] = useState("");
    const [notes, setNotes] = useState("");
    // The persisted pet we're currently looking at — null until the first
    // save in create mode, since a photo can only be attached to a pet
    // that already has an id.
    const [savedPet, setSavedPet] = useState<Pet | null>(pet);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!open) return;
        setName(pet?.name || "");
        setSpecies(pet?.species || "");
        setBreed(pet?.breed || "");
        setBirthday(pet?.birthday ? pet.birthday.slice(0, 10) : "");
        setGender(pet?.gender || "");
        setWeight(pet?.weight_kg != null ? String(Number(pet.weight_kg)) : "");
        setNotes(pet?.notes || "");
        setSavedPet(pet);
        setError(null);
    }, [open, pet]);

    const handleSave = async () => {
        if (!name.trim() || !species.trim() || weightInvalid) return;
        setSaving(true);
        setError(null);
        try {
            // Empty optional fields are sent as null so clearing one sticks.
            const payload: PetPayload & { name: string; species: string } = {
                name: name.trim(),
                species: species.trim(),
                breed: breed.trim() || null,
                birthday: birthday || null,
                gender: gender || null,
                weight_kg: weight.trim() ? Number(weight) : null,
                notes: notes.trim() || null,
            };
            const result = savedPet
                ? await petService.updatePet(savedPet.pet_profile_id, payload)
                : await petService.createPet(customerId, payload);
            setSavedPet(result);
            onSaved(result);
        } catch (err: any) {
            setError(err?.response?.data?.detail || err.message || "Failed to save pet");
        } finally {
            setSaving(false);
        }
    };

    const weightNumber = Number(weight);
    const weightInvalid =
        weight.trim() !== "" && (!Number.isFinite(weightNumber) || weightNumber <= 0 || weightNumber > 200);

    const handlePhotoUpload = async (files: File[]) => {
        if (!savedPet || files.length === 0) return;
        const result = await petService.uploadPetPhoto(savedPet.pet_profile_id, files[0]);
        setSavedPet(result);
        onSaved(result);
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{pet ? "Edit Pet" : "Add Pet"}</DialogTitle>
            <DialogContent>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                        {error}
                    </Alert>
                )}

                <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <Avatar src={savedPet?.photo_url} sx={{ width: 56, height: 56 }}>
                            <PetsIcon />
                        </Avatar>
                        <Typography variant="body2" color="text.secondary">
                            {savedPet
                                ? "Add or replace this pet's photo below."
                                : "Save the pet first, then add a photo."}
                        </Typography>
                    </Box>

                    <Box sx={{ display: "flex", gap: 2 }}>
                        <TextField
                            label="Name"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            required
                            fullWidth
                            size="small"
                        />
                        <Autocomplete
                            freeSolo
                            options={SPECIES_SUGGESTIONS}
                            inputValue={species}
                            onInputChange={(_, value) => setSpecies(value)}
                            fullWidth
                            renderInput={(params) => (
                                <TextField {...params} label="Species" required size="small" />
                            )}
                        />
                    </Box>

                    <Box sx={{ display: "flex", gap: 2 }}>
                        <TextField
                            label="Breed (optional)"
                            value={breed}
                            onChange={(e) => setBreed(e.target.value)}
                            fullWidth
                            size="small"
                        />
                        <TextField
                            label="Birthday (optional)"
                            type="date"
                            value={birthday}
                            onChange={(e) => setBirthday(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ max: new Date().toISOString().slice(0, 10) }}
                            fullWidth
                            size="small"
                        />
                    </Box>

                    <Box sx={{ display: "flex", gap: 2 }}>
                        <TextField
                            select
                            label="Gender (optional)"
                            value={gender}
                            onChange={(e) => setGender(e.target.value as "" | "male" | "female")}
                            fullWidth
                            size="small"
                        >
                            <MenuItem value="">Not set</MenuItem>
                            <MenuItem value="female">Female</MenuItem>
                            <MenuItem value="male">Male</MenuItem>
                        </TextField>
                        <TextField
                            label="Weight (optional)"
                            type="number"
                            value={weight}
                            onChange={(e) => setWeight(e.target.value)}
                            error={weightInvalid}
                            helperText={weightInvalid ? "Between 0 and 200 kg" : undefined}
                            InputProps={{ endAdornment: <InputAdornment position="end">kg</InputAdornment> }}
                            inputProps={{ min: 0, max: 200, step: 0.1, inputMode: "decimal" }}
                            fullWidth
                            size="small"
                        />
                    </Box>

                    <TextField
                        label="Notes (optional)"
                        placeholder="Food preferences, allergies, anything to know"
                        value={notes}
                        onChange={(e) => setNotes(e.target.value.slice(0, 500))}
                        helperText={`${notes.length}/500`}
                        multiline
                        minRows={2}
                        fullWidth
                        size="small"
                    />

                    <Button
                        type="button"
                        variant="contained"
                        onClick={handleSave}
                        disabled={saving || !name.trim() || !species.trim() || weightInvalid}
                        sx={{ alignSelf: "flex-start" }}
                    >
                        {saving ? "Saving..." : savedPet ? "Save Changes" : "Save Pet"}
                    </Button>

                    {savedPet && (
                        <Box sx={{ mt: 1 }}>
                            <ImageUploadZone onUpload={handlePhotoUpload} maxFiles={1} />
                        </Box>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
};
